import type { StationLicense, StationLicenseState, StationLicenseType } from "@station/contracts";
import type { Tone } from "@station/ui";

/**
 * İstasyon satış belgeleri (tütün / alkol) ekranının saf mantığı (Prompt 16 — web, aşama B; ADR 0016 §18). Belge
 * v1'de satışı ENGELLEMEZ; durum (geçerli / yakında bitecek / süresi dolmuş) sunucuda market ayarındaki uyarı günüyle
 * hesaplanır ve rozet durumu METİNLE yazar.
 */

export const LICENSE_STATE_TONE: Record<StationLicenseState, Tone> = {
  VALID: "success",
  EXPIRING: "warning",
  EXPIRED: "danger",
  NOT_YET_VALID: "info",
  INACTIVE: "neutral",
};

export interface LicenseFormValues {
  stationId: string;
  licenseType: StationLicenseType;
  documentNo: string;
  issuer: string;
  issuedOn: string | null;
  validFrom: string | null;
  /** Boş = süresiz. */
  validUntil: string | null;
  fileId: string | null;
  /** Yalnızca gösterim (yüklenen / mevcut dosyanın adı). */
  fileName: string | null;
  note: string;
}

export function emptyLicenseForm(stationId: string): LicenseFormValues {
  return {
    stationId,
    licenseType: "TOBACCO_SALES",
    documentNo: "",
    issuer: "",
    issuedOn: null,
    validFrom: null,
    validUntil: null,
    fileId: null,
    fileName: null,
    note: "",
  };
}

export function licenseFormFrom(license: StationLicense): LicenseFormValues {
  return {
    stationId: license.stationId,
    licenseType: license.licenseType,
    documentNo: license.documentNo,
    issuer: license.issuer ?? "",
    issuedOn: license.issuedOn,
    validFrom: license.validFrom,
    validUntil: license.validUntil,
    fileId: license.fileId,
    fileName: license.fileName,
    note: license.note ?? "",
  };
}

export type LicenseProblem = "documentNo" | "validFrom" | "validUntil";

/**
 * Form → gövde. Oluşturmada istasyon gövdededir; düzenlemede istasyon DEĞİŞMEZ (gövdeye çıkmaz) ve iyimser kilit
 * sürümü eklenir. Boş isteğe bağlı alanlar `null`; dosya adı yalnızca gösterimdir (gövdeye çıkmaz).
 */
export function licenseBody(
  values: LicenseFormValues,
  existing: Pick<StationLicense, "version"> | null,
): { body: Record<string, unknown>; problems: LicenseProblem[] } {
  const problems: LicenseProblem[] = [];
  const documentNo = values.documentNo.trim();
  if (documentNo === "") problems.push("documentNo");
  if (!values.validFrom) problems.push("validFrom");
  if (values.validFrom && values.validUntil && values.validUntil < values.validFrom)
    problems.push("validUntil");
  const blank = (text: string) => (text.trim() === "" ? null : text.trim());
  const fields = {
    licenseType: values.licenseType,
    documentNo,
    issuer: blank(values.issuer),
    issuedOn: values.issuedOn,
    validFrom: values.validFrom ?? "",
    validUntil: values.validUntil,
    fileId: values.fileId,
    note: blank(values.note),
  };
  return {
    body: existing ? { ...fields, version: existing.version } : { stationId: values.stationId, ...fields },
    problems,
  };
}

/** Kalan gün metninin anahtarı ve sayısı (süresiz belge → null). */
export function licenseDays(
  license: Pick<StationLicense, "daysToExpiry" | "validUntil">,
): { key: "left" | "ago" | "today"; count: number } | null {
  if (license.validUntil === null || license.daysToExpiry === null) return null;
  if (license.daysToExpiry === 0) return { key: "today", count: 0 };
  return license.daysToExpiry > 0
    ? { key: "left", count: license.daysToExpiry }
    : { key: "ago", count: -license.daysToExpiry };
}
